import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

const featureIcons = [
  'ri-table-line',
  'ri-user-search-line',
  'ri-guide-line',
  'ri-chat-quote-line',
  'ri-truck-line',
  'ri-shield-check-line',
];

const featureImages = [
  'https://readdy.ai/api/search-image?query=An%20organized%20product%20catalog%20spreadsheet%20shown%20on%20a%20laptop%20screen%20beside%20folded%20clothing%20and%20sneakers%20on%20a%20clean%20warm%20beige%20desk%2C%20soft%20natural%20lighting%2C%20minimal%20editorial%20flat%20lay%2C%20crisp%20readable%20interface%2C%20calm%20productive%20mood%2C%20clean%20composition&width=600&height=400&seq=feature-spreadsheet-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20small%20network%20of%20connected%20profile%20cards%20and%20storefront%20icons%20arranged%20on%20a%20soft%20sage%20green%20surface%20with%20a%20magnifying%20glass%2C%20soft%20daylight%2C%20minimal%20concept%20illustration%2C%20clean%20modern%20flat%20design%2C%20trustworthy%20research%20mood%2C%20balanced%20composition&width=600&height=400&seq=feature-seller-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20stack%20of%20open%20fashion%20and%20shopping%20magazines%20with%20colorful%20bookmarks%20on%20a%20light%20linen%20surface%20beside%20a%20cup%20of%20tea%2C%20soft%20window%20light%2C%20minimal%20editorial%20flat%20lay%2C%20warm%20inviting%20reading%20mood%2C%20crisp%20page%20detail%2C%20clean%20composition&width=600&height=400&seq=feature-guides-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Five%20golden%20star%20rating%20icons%20floating%20above%20a%20clean%20product%20card%20with%20subtle%20speech%20bubble%20outlines%20on%20a%20soft%20peach%20background%2C%20warm%20ambient%20light%2C%20minimal%20review%20concept%20illustration%2C%20friendly%20trustworthy%20mood%2C%20balanced%20composition&width=600&height=400&seq=feature-reviews-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=Several%20neatly%20sealed%20kraft%20shipping%20boxes%20with%20a%20small%20delivery%20van%20toy%20on%20a%20pale%20blue%20grey%20surface%2C%20soft%20studio%20light%2C%20minimal%20logistics%20flat%20lay%20photography%2C%20crisp%20cardboard%20texture%2C%20efficient%20organized%20mood%2C%20clean%20composition&width=600&height=400&seq=feature-shipping-01&orientation=landscape',
  'https://readdy.ai/api/search-image?query=A%20minimalist%20shield%20and%20padlock%20icon%20composition%20with%20soft%20sage%20green%20accents%20on%20a%20clean%20warm%20cream%20background%2C%20gentle%20natural%20light%2C%20modern%20security%20concept%20illustration%2C%20calm%20reassuring%20mood%2C%20balanced%20composition&width=600&height=400&seq=feature-safety-01&orientation=landscape',
];

const popularIcons = [
  'ri-chat-quote-line',
  'ri-truck-line',
  'ri-coupon-2-line',
  'ri-discount-percent-line',
  'ri-archive-line',
  'ri-verified-badge-line',
  'ri-table-line',
  'ri-user-search-line',
];

export default function InfoSections() {
  const { t } = useTranslation();

  const features = [1, 2, 3, 4, 5, 6];
  const popular = [1, 2, 3, 4, 5, 6, 7, 8];

  return (
    <>
      <section className="bg-background-100">
        <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-10 px-4 py-16 md:grid-cols-2 md:px-6 md:py-20">
          <div>
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">
              {t('whatis.title')}
            </h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground-600 md:text-base">
              {t('whatis.copy')}
            </p>
            <Link
              to="/article/what-is-usfans-spreadsheet"
              className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              {t('whatis.cta')}
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
          <div className="grid grid-cols-2 gap-4">
            {['shoes', 'hoodies', 'tshirts', 'jackets'].map((id) => {
              const cat = categories.find((c) => c.id === id);
              return cat ? (
                <a
                  key={id}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-primary-400"
                >
                  <img src={cat.image} alt={t(cat.nameKey)} className="h-36 w-full object-cover object-top" />
                  <p className="px-3 py-2 text-sm font-medium text-foreground-800">{t(cat.nameKey)}</p>
                </a>
              ) : null;
            })}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{t('why.title')}</h2>
          <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600">{t('why.subtitle')}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {features.map((n) => (
            <div
              key={n}
              className="group overflow-hidden rounded-lg border border-background-200 bg-background-50 transition-colors hover:border-secondary-400"
            >
              <div className="relative h-36 w-full overflow-hidden">
                <img
                  src={featureImages[n - 1]}
                  alt={t(`why.card${n}.title`)}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
                />
              </div>
              <div className="p-5">
                <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                  <i className={`${featureIcons[n - 1]} text-lg`} />
                </span>
                <h3 className="mt-4 text-base font-semibold text-foreground-950">
                  {t(`why.card${n}.title`)}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {t(`why.card${n}.desc`)}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section id="reviews" className="bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-16 md:px-6 md:py-20">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-foreground-950 md:text-3xl">{t('popular.title')}</h2>
            <p className="mx-auto mt-3 max-w-xl text-sm text-foreground-600">{t('popular.subtitle')}</p>
          </div>

          <div className="mt-10 flex flex-wrap justify-center gap-3">
            {popular.map((n) => (
              <span
                key={n}
                className="inline-flex items-center gap-2 rounded-full border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800"
              >
                <span className="flex h-5 w-5 items-center justify-center text-accent-600">
                  <i className={`${popularIcons[n - 1]} text-sm`} />
                </span>
                {t(`popular.${n}`)}
              </span>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}